import { Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import { VacationRequestDialogComponent } from '../vacation-request-dialog/vacation-request-dialog.component';

export interface DriverPetitionWeek {
  weekNumber: number;
  year: number;
  weekDisplay: string;
  dayCount: number;
  dates: string[];
  startDate: string;
  endDate: string;
}

export interface DriverPetition {
  id: number;
  uuid: string;
  startDate: string;
  endDate: string;
  days: number;
  personal: boolean;
  dates?: string[]; // Array of actual dates in YYYY-MM-DD format
  weeks?: DriverPetitionWeek[]; // Weeks breakdown
}

@Component({
  selector: 'app-driver-petition',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './driver-petition.component.html',
  styleUrls: ['./driver-petition.component.css', '../../../styles/layout.css'],
})
export class DriverPetitionComponent {
  petitions = input.required<DriverPetition>();
  pending = input<boolean>();
  driverId = input<number>();

  petitionRejected = output<void>();

  private readonly holidaysService = inject(HolidaysService);
  private readonly dialog = inject(MatDialog);

  /**
   * Format dates for display. If dates are continuous, show as range.
   * If dates have gaps, show individual ranges or dates.
   */
  getFormattedDates(): string {
    const petition = this.petitions();

    // If no dates array provided, fall back to simple range
    if (!petition.dates || petition.dates.length === 0) {
      return `${petition.startDate} - ${petition.endDate}`;
    }

    // Convert YYYY-MM-DD to Date objects and sort
    const sortedDates = petition.dates
      .map(d => new Date(d))
      .sort((a, b) => a.getTime() - b.getTime());

    // Check if dates are continuous
    const isContinuous = this.areDatesContinuous(sortedDates);

    if (isContinuous) {
      // Show simple range
      return `${petition.startDate} - ${petition.endDate}`;
    } else {
      // Show grouped ranges
      return this.formatNonContinuousDates(sortedDates);
    }
  }

  /**
   * Check if dates are continuous (no gaps)
   */
  private areDatesContinuous(dates: Date[]): boolean {
    for (let i = 1; i < dates.length; i++) {
      const prevDate = new Date(dates[i - 1]);
      const currentDate = new Date(dates[i]);

      // Add one day to previous date
      prevDate.setDate(prevDate.getDate() + 1);

      // If current date is not the next day, dates are not continuous
      if (prevDate.getTime() !== currentDate.getTime()) {
        return false;
      }
    }
    return true;
  }

  /**
   * Format non-continuous dates as grouped ranges
   */
  private formatNonContinuousDates(dates: Date[]): string {
    const ranges: string[] = [];
    let rangeStart = dates[0];
    let rangeEnd = dates[0];

    for (let i = 1; i < dates.length; i++) {
      const prevDate = new Date(rangeEnd);
      prevDate.setDate(prevDate.getDate() + 1);

      if (prevDate.getTime() === dates[i].getTime()) {
        // Continue the current range
        rangeEnd = dates[i];
      } else {
        // End current range and start a new one
        ranges.push(this.formatDateRange(rangeStart, rangeEnd));
        rangeStart = dates[i];
        rangeEnd = dates[i];
      }
    }

    // Add the last range
    ranges.push(this.formatDateRange(rangeStart, rangeEnd));

    return ranges.join(', ');
  }

  /**
   * Format a single date range
   */
  private formatDateRange(start: Date, end: Date): string {
    const startStr = this.formatDateToDisplay(start);

    if (start.getTime() === end.getTime()) {
      // Single day
      return startStr;
    } else {
      // Range
      const endStr = this.formatDateToDisplay(end);
      return `${startStr} - ${endStr}`;
    }
  }

  /**
   * Format Date to DD/MM/YYYY
   */
  private formatDateToDisplay(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  validatorPetition() {
    const petition = this.petitions();
    const driverId = this.driverId();

    if (!driverId) {
      return;
    }

    // Use actual dates from petition if available, otherwise generate range
    const dates = petition.dates || this.generateDateRange(petition.startDate, petition.endDate);

    const data = {
      driver_assignment: driverId,
      dates: dates,
    };

    this.holidaysService.validateRules(data).subscribe({
      next: (response) => {
        console.log('Petition approved successfully:', response);
      },
      error: (error) => {
        console.error('Error approving petition:', error);
      },
    });

    this.holidaysService.validateSlots(data).subscribe({
      next: (response) => {
        console.log('Slots validated successfully:', response);
      },
      error: (error) => {
        console.error('Error validating slots:', error);
      },
    });
  }

  approvePetition() {
    const uuid = this.petitions().uuid;

    if (!uuid) {
      return;
    }

    const dialogRef = this.dialog.open(VacationRequestDialogComponent, {
      data: {
        title: 'Aprobar petición',
        message: '¿Está seguro que desea aprobar esta petición de vacaciones?',
        confirmText: 'Aprobar',
        cancelText: 'Cancelar',
        toastSuccessMessage: 'Petición aprobada correctamente',
        toastErrorMessage: 'Error al aprobar la petición',
        handler: (comment?: string) =>
          this.holidaysService.acceptPetition(uuid),
        isAcceptAction: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.petitionRejected.emit();
      }
    });
  }

  rejectPetition() {
    const uuid = this.petitions().uuid;

    if (!uuid) {
      return;
    }

    const dialogRef = this.dialog.open(VacationRequestDialogComponent, {
      data: {
        title: 'Rechazar petición',
        message: '¿Está seguro que desea rechazar esta petición de vacaciones?',
        confirmText: 'Rechazar',
        cancelText: 'Cancelar',
        toastSuccessMessage: 'Petición rechazada correctamente',
        toastErrorMessage: 'Error al rechazar la petición',
        reject: true,
        handler: (comment?: string) =>
          this.holidaysService.rejectPetition(uuid, comment),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.petitionRejected.emit();
      }
    });
  }

  private generateDateRange(
    startDateStr: string,
    endDateStr: string
  ): string[] {
    // Parse DD/MM/YYYY format to Date objects
    const [startDay, startMonth, startYear] = startDateStr
      .split('/')
      .map(Number);
    const [endDay, endMonth, endYear] = endDateStr.split('/').map(Number);

    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);

    const dates: string[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');

      dates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }
}
